import React, { useState, useEffect } from "react";
import { Code2, Plus, SlidersHorizontal, Trash2, Upload } from "lucide-react";
import QuestionList from "./QuestionList";
import QuestionForm from "./QuestionForm";
import ImportQuestionsModal from "./ImportQuestionsModal";
import "./CodingQuestionBank.scss";
import apiClient from "../../utils/apiClient";
import { PageHeader, ConfirmModal, AppButton, SearchInput } from "../ui";
import { FilterModal, FilterGroup } from "../ui/FilterModal";

const DIFFICULTIES = ["", "Easy", "Medium", "Hard"];
const SORT_OPTIONS = [
  { value: "newest", label: "Newest First" },
  { value: "oldest", label: "Oldest First" },
  { value: "title-asc", label: "Title A → Z" },
  { value: "title-desc", label: "Title Z → A" },
];

const CodingQuestionBankPage = ({ mode = "institute" }) => {
  const isLibrary = mode === "library";
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [filters, setFilters] = useState({ category: "", label: "", difficulty: "", search: "", sort: "newest" });
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [filterMeta, setFilterMeta] = useState({ categories: [], labels: [] });
  const [deleteQuestionId, setDeleteQuestionId] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [saveError, setSaveError] = useState("");

  // Fetch filter options from server
  useEffect(() => {
    (async () => {
      try {
        const { data } = await apiClient.get("/questions/filters");
        setFilterMeta(data);
      } catch { /* silent */ }
    })();
  }, [refreshKey]);

  // Save handler for add/edit (calls API)
  const handleSave = async (question) => {
    try {
      if (question._id) {
        await apiClient.put(`/questions/${question._id}`, question);
      } else {
        await apiClient.post("/questions/add", question);
      }
      setRefreshKey(k => k + 1);
      setEditingQuestion(null);
    } catch (err) {
      setSaveError(err?.response?.data?.message || "Failed to save question. Please try again.");
    }
  };

  // Delete handler
  const handleDelete = (id) => setDeleteQuestionId(id);

  const confirmDelete = async () => {
    const id = deleteQuestionId;
    setDeleteQuestionId(null);
    try {
      await apiClient.delete(`/questions/${id}`);
      setRefreshKey(k => k + 1);
    } catch (err) {
      setSaveError(err?.response?.data?.message || "Failed to delete question.");
    }
  };

  const confirmBulkDelete = async () => {
    const ids = selectedIds;
    setShowBulkDeleteConfirm(false);
    if (ids.length === 0) return;
    try {
      await apiClient.post("/questions/bulk-delete", { ids });
      setSelectedIds([]);
      setRefreshKey(k => k + 1);
    } catch (err) {
      setSaveError(err?.response?.data?.message || "Failed to delete questions.");
    }
  };

  const handleCancel = () => setEditingQuestion(null);

  const handleResetFilters = () => {
    setFilters(f => ({ ...f, category: "", label: "", difficulty: "", sort: "newest" }));
  };

  const activeFilterCount = (filters.category ? 1 : 0) + (filters.label ? 1 : 0) + (filters.difficulty ? 1 : 0);

  return (
  <>
    <div className="cqb-page">
      {saveError && (
        <div className="alert alert-danger py-2 mb-3 small" role="alert">
          {saveError}
          <button type="button" className="btn-close float-end" onClick={() => setSaveError("")} aria-label="Close" />
        </div>
      )}
      {!editingQuestion ? (
        <>
          <PageHeader
            icon={Code2}
            title={isLibrary ? "Platform Coding Questions Library" : "Coding Question Bank"}
            description={isLibrary
              ? "Author coding questions that every institute can use. Mark a question as Public to publish it."
              : "Manage your library of coding interview questions, test cases, and labels"}
          />

          {/* Compact Toolbar */}
          <div className="cqb-toolbar">
            <AppButton onClick={() => setEditingQuestion({})}>
              <Plus size={16} />
              <span>Add Question</span>
            </AppButton>

            <AppButton variant="outline" onClick={() => setShowImportModal(true)}>
              <Upload size={16} />
              <span>Import CSV</span>
            </AppButton>

            {selectedIds.length > 0 && (
              <AppButton variant="destructive" onClick={() => setShowBulkDeleteConfirm(true)}>
                <Trash2 size={16} />
                <span>Delete Selected ({selectedIds.length})</span>
              </AppButton>
            )}

            <SearchInput
              value={filters.search}
              onChange={(val) => setFilters(f => ({ ...f, search: val }))}
              placeholder="Search by title/desc..."
              className="cqb-search"
            />

            <div className="cqb-toolbar-actions">
              {/* Desktop inline filters */}
              <select
                className="cqb-filter-select d-none d-md-block"
                value={filters.category}
                onChange={(e) => setFilters(f => ({ ...f, category: e.target.value }))}
              >
                <option value="">All Categories</option>
                {filterMeta.categories.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              <select
                className="cqb-filter-select d-none d-md-block"
                value={filters.label}
                onChange={(e) => setFilters(f => ({ ...f, label: e.target.value }))}
              >
                <option value="">All Labels</option>
                {filterMeta.labels.map(l => (
                  <option key={l} value={l}>{l}</option>
                ))}
              </select>
              <select
                className="cqb-filter-select d-none d-md-block"
                value={filters.difficulty}
                onChange={(e) => setFilters(f => ({ ...f, difficulty: e.target.value }))}
              >
                {DIFFICULTIES.map((d, idx) => (
                  <option key={d || idx} value={d}>{d || "All Difficulties"}</option>
                ))}
              </select>
              <select
                className="cqb-filter-select d-none d-md-block"
                value={filters.sort}
                onChange={(e) => setFilters(f => ({ ...f, sort: e.target.value }))}
              >
                {SORT_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>

              {/* Mobile filter button */}
              <button
                className={`cqb-filter-btn d-md-none${activeFilterCount > 0 ? " cqb-filter-btn--active" : ""}`}
                onClick={() => setShowFilterModal(true)}
                aria-label="Open filters"
              >
                <SlidersHorizontal size={16} />
                {activeFilterCount > 0 && (
                  <span className="cqb-filter-badge">{activeFilterCount}</span>
                )}
              </button>
            </div>
          </div>

          <QuestionList
            filters={filters}
            refreshKey={refreshKey}
            onEdit={setEditingQuestion}
            onDelete={handleDelete}
            mode={mode}
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
          />

          {/* Mobile Filter Modal */}
          <FilterModal
            show={showFilterModal}
            onHide={() => setShowFilterModal(false)}
            onReset={handleResetFilters}
          >
            <FilterGroup label="Category">
              <select
                value={filters.category}
                onChange={(e) => setFilters(f => ({ ...f, category: e.target.value }))}
              >
                <option value="">All Categories</option>
                {filterMeta.categories.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </FilterGroup>
            <FilterGroup label="Label">
              <select
                value={filters.label}
                onChange={(e) => setFilters(f => ({ ...f, label: e.target.value }))}
              >
                <option value="">All Labels</option>
                {filterMeta.labels.map(l => (
                  <option key={l} value={l}>{l}</option>
                ))}
              </select>
            </FilterGroup>
            <FilterGroup label="Difficulty">
              <select
                value={filters.difficulty}
                onChange={(e) => setFilters(f => ({ ...f, difficulty: e.target.value }))}
              >
                {DIFFICULTIES.map((d, idx) => (
                  <option key={d || idx} value={d}>{d || "All Difficulties"}</option>
                ))}
              </select>
            </FilterGroup>
            <FilterGroup label="Sort By">
              <select
                value={filters.sort}
                onChange={(e) => setFilters(f => ({ ...f, sort: e.target.value }))}
              >
                {SORT_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </FilterGroup>
          </FilterModal>
        </>
      ) : (
        <QuestionForm
          question={editingQuestion._id ? editingQuestion : null}
          onSave={handleSave}
          onCancel={handleCancel}
          mode={mode}
        />
      )}
    </div>

    <ConfirmModal
      show={!!deleteQuestionId}
      onHide={() => setDeleteQuestionId(null)}
      onConfirm={confirmDelete}
      title="Delete Question"
      message="Are you sure you want to delete this question?"
      confirmText="Delete"
      variant="danger"
    />

    <ConfirmModal
      show={showBulkDeleteConfirm}
      onHide={() => setShowBulkDeleteConfirm(false)}
      onConfirm={confirmBulkDelete}
      title="Delete Selected Questions"
      message={`Are you sure you want to delete ${selectedIds.length} question${selectedIds.length === 1 ? "" : "s"}? This cannot be undone.`}
      confirmText="Delete"
      variant="danger"
    />

    <ImportQuestionsModal
      show={showImportModal}
      mode={mode}
      onHide={() => setShowImportModal(false)}
      onImported={() => {
        setShowImportModal(false);
        setRefreshKey((k) => k + 1);
      }}
    />
  </>
  );
};

export default CodingQuestionBankPage;
